// Kompaktowa tabela dostępności pokoi wzorowana na AccommodationSection

import React, { useState } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import { PriceViewMode } from "../../types/pricing.types";
import {
  AssignedRoom,
  // AssignmentType,
  MinAvailabilityMap,
} from "../../types/accommodation.types";
import AvailabilityTableRow from "./AvailabilityTableRow";

interface AvailabilityTableProps {
  availableRooms: MinAvailabilityMap;
  viewMode: PriceViewMode;
  assignedRooms: AssignedRoom[];
  showUnavailable: boolean;
}

const AvailabilityTable: React.FC<AvailabilityTableProps> = ({
  availableRooms,
  viewMode,
  assignedRooms,
  showUnavailable,
}) => {
  const { accommodationDays, markup, availabilityData } = useAccommodation();
  const strategy = availabilityData?.strategy;
  const restrictionInfo = availabilityData?.restrictionInfo;
  const [expandedRoom, setExpandedRoom] = useState<string | null>(null);

  // Filtruj pokoje według opcji wyświetlania
  const filteredRooms = React.useMemo(() => {
    let rooms = Object.entries(availableRooms);

    if (!showUnavailable) {
      rooms = rooms.filter(([, room]) => room.minCount > 0);
    }

    return rooms;
  }, [availableRooms, showUnavailable]);

  // Przygotuj dni noclegowe (bez checkout)
  const accommodationDaysOnly = React.useMemo(() => {
    return accommodationDays.slice(0, -1);
  }, [accommodationDays]);

  // Formatuj nagłówek dnia
  const formatDayHeader = (day: any, index: number) => {
    const date = new Date(day.date);
    const dayName = date.toLocaleDateString("pl-PL", { weekday: "short" });
    const dayMonth = date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
    });

    return (
      <div className="text-center">
        <div className="text-sm">Dzień {index + 1}</div>
        <div className="text-xs text-gray-500">{dayMonth}</div>
        <div className="text-xs text-gray-500">({dayName})</div>
      </div>
    );
  };

  // Renderuj nagłówek tabeli w zależności od viewMode
  const renderTableHeader = () => {
    if (viewMode === "availability") {
      return (
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left py-3 px-4 font-medium text-gray-900 w-1/2">
              Pokój
            </th>
            {accommodationDaysOnly.map((day, index) => (
              <th
                key={day.id}
                className="text-center py-3 px-2 font-medium text-gray-900 w-20"
              >
                {formatDayHeader(day, index)}
              </th>
            ))}
            <th className="text-center py-3 px-2 font-medium text-gray-900 w-16">
              <div className="text-center">
                <div className="text-sm">MIN</div>
                <div className="text-xs text-gray-500">dostępność</div>
              </div>
            </th>
            <th className="text-center py-3 px-3 font-medium text-gray-900 w-20">
              <div className="text-center">
                <div className="text-sm">Maks. osób</div>
                <div className="text-xs text-gray-500">w pokoju</div>
              </div>
            </th>
          </tr>
        </thead>
      );
    }

    if (viewMode === "base_prices" || viewMode === "final_prices") {
      return (
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left py-3 px-4 font-medium text-gray-900 w-1/2">
              Pokój
            </th>
            {accommodationDaysOnly.map((day, index) => (
              <th
                key={day.id}
                className="text-center py-3 px-2 font-medium text-gray-900"
              >
                {formatDayHeader(day, index)}
              </th>
            ))}
          </tr>
        </thead>
      );
    }

    // Fallback - nie powinno się nigdy zdarzyć
    return (
      <thead className="bg-gray-50">
        <tr>
          <th className="text-left py-3 px-4 font-medium text-gray-900">
            Pokój
          </th>
        </tr>
      </thead>
    );
  };

  const handleRoomToggle = (categoryOptionId: string) => {
    // Pozwól na rozwijanie tylko w trybie availability
    if (viewMode === "availability") {
      setExpandedRoom((prev) =>
        prev === categoryOptionId ? null : categoryOptionId
      );
    }
  };

  if (filteredRooms.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">🏨</div>
        <p className="text-gray-500">
          {showUnavailable ? "Brak pokoi w systemie" : "Brak dostępnych pokoi"}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        {renderTableHeader()}
        <tbody>
          {filteredRooms.map(([categoryOptionId, room]) => (
            <AvailabilityTableRow
              key={categoryOptionId}
              categoryOptionId={categoryOptionId}
              room={room}
              viewMode={viewMode}
              assignedRooms={assignedRooms}
              accommodationDays={accommodationDaysOnly}
              markup={markup}
              isExpanded={expandedRoom === categoryOptionId}
              onToggleExpanded={handleRoomToggle}
              strategy={strategy}
              restrictionInfo={restrictionInfo}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AvailabilityTable;
